// API helper for making authenticated requests to the Rails backend

const getCSRFToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute("content") : ""
}

const defaultHeaders = () => ({
  "Content-Type": "application/json",
  "Accept": "application/json",
  "X-CSRF-Token": getCSRFToken(),
})

const handleResponse = async (response) => {
  if (response.status === 204) {
    return null
  }

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data.error || data.errors?.join(", ") || "Request failed")
    error.status = response.status
    error.data = data
    throw error
  }

  return data
}

export const api = {
  get: async (path) => {
    const response = await fetch(path, {
      method: "GET",
      headers: defaultHeaders(),
      credentials: "same-origin",
    })
    return handleResponse(response)
  },

  post: async (path, body = {}) => {
    const response = await fetch(path, {
      method: "POST",
      headers: defaultHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
    return handleResponse(response)
  },

  patch: async (path, body = {}) => {
    const response = await fetch(path, {
      method: "PATCH",
      headers: defaultHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
    return handleResponse(response)
  },

  put: async (path, body = {}) => {
    const response = await fetch(path, {
      method: "PUT",
      headers: defaultHeaders(),
      credentials: "same-origin",
      body: JSON.stringify(body),
    })
    return handleResponse(response)
  },

  delete: async (path) => {
    const response = await fetch(path, {
      method: "DELETE",
      headers: defaultHeaders(),
      credentials: "same-origin",
    })
    return handleResponse(response)
  },
}

// Specific API endpoints
export const dashboardApi = {
  get: () => api.get("/api/v1/dashboard"),
}

export const coursesApi = {
  list: () => api.get("/api/v1/courses"),
  create: (data) => api.post("/api/v1/courses", { course: data }),
  update: (id, data) => api.patch(`/api/v1/courses/${id}`, { course: data }),
  delete: (id) => api.delete(`/api/v1/courses/${id}`),
}

export const termsApi = {
  list: () => api.get("/api/v1/terms"),
}

export const assignmentsApi = {
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString()
    return api.get(`/api/v1/assignments${query ? `?${query}` : ""}`)
  },
  get: (id) => api.get(`/api/v1/assignments/${id}`),
  create: (courseId, data) => api.post("/api/v1/assignments", { course_id: courseId, assignment: data }),
  update: (id, data) => api.patch(`/api/v1/assignments/${id}`, { assignment: data }),
  delete: (id) => api.delete(`/api/v1/assignments/${id}`),
  toggleComplete: (id) => api.patch(`/api/v1/assignments/${id}/toggle_complete`),
}

// LMS connections (replaces canvasSettingsApi and syncApi)
export const lmsConnectionsApi = {
  list: () => api.get("/api/v1/lms_connections"),
  get: (id) => api.get(`/api/v1/lms_connections/${id}`),
  create: (data) => api.post("/api/v1/lms_connections", data),
  update: (id, data) => api.patch(`/api/v1/lms_connections/${id}`, data),
  delete: (id) => api.delete(`/api/v1/lms_connections/${id}`),
  sync: (id) => api.post(`/api/v1/lms_connections/${id}/sync`),
}

export const authApi = {
  signIn: (email, password) => api.post("/users/sign_in", { user: { email, password } }),
  signUp: (email, password, passwordConfirmation, { name, role, timezone, notificationTime, termsAcceptedAt } = {}) => api.post("/users", {
    user: {
      email,
      password,
      password_confirmation: passwordConfirmation,
      name,
      role,
      timezone,
      notification_time: notificationTime,
      terms_accepted_at: termsAcceptedAt
    }
  }),
  signOut: () => api.delete("/users/sign_out"),
  forgotPassword: (email) => api.post("/users/password", { user: { email } }),
  resetPassword: (resetToken, password, passwordConfirmation) => api.put("/users/password", {
    user: {
      reset_password_token: resetToken,
      password,
      password_confirmation: passwordConfirmation
    }
  }),
}

// Parent invitation system
export const invitationsApi = {
  list: () => api.get("/api/v1/invitations"),
  create: (parentEmail) => api.post("/api/v1/invitations", { invitation: { parent_email: parentEmail } }),
  get: (token) => api.get(`/api/v1/invitations/${token}`),
  revoke: (id) => api.delete(`/api/v1/invitations/${id}`),
}

export const invitationAcceptanceApi = {
  accept: (token, { email, password, passwordConfirmation, name, termsAcceptedAt } = {}) =>
    api.post("/api/v1/invitation_acceptances", {
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
      name,
      terms_accepted_at: termsAcceptedAt,
    }),
}

// Parent dashboard
export const parentDashboardApi = {
  get: (termId = null) => {
    const params = termId ? `?term_id=${termId}` : ""
    return api.get(`/api/v1/parent_dashboard${params}`)
  },
  getStudentDetail: (studentId) => api.get(`/api/v1/parent_dashboard/students/${studentId}`),
  requestSync: (studentId) => api.post("/api/v1/parent_dashboard/request_sync", { student_id: studentId }),
}

// Link management
export const linkedStudentsApi = {
  list: () => api.get("/api/v1/linked_students"),
  unlink: (studentId) => api.delete(`/api/v1/linked_students/${studentId}`),
}

export const linkedParentsApi = {
  list: () => api.get("/api/v1/linked_parents"),
  unlink: (parentId) => api.delete(`/api/v1/linked_parents/${parentId}`),
}

// Notification preferences
export const notificationPreferencesApi = {
  get: () => api.get("/api/v1/notification_preferences"),
  update: (data) => api.patch("/api/v1/notification_preferences", data),
  updatePhone: (phoneNumber) => api.post("/api/v1/notification_preferences/update_phone", { phone_number: phoneNumber }),
  verifyPhone: (code) => api.post("/api/v1/notification_preferences/verify_phone", { code }),
  sendTest: () => api.post("/api/v1/notification_preferences/send_test"),
}

// Parent notification subscriptions
export const parentNotificationSubscriptionsApi = {
  list: () => api.get("/api/v1/parent_notification_subscriptions"),
  update: (studentId, data) => api.patch(`/api/v1/parent_notification_subscriptions/${studentId}/update`, data),
}

// Notifications
export const notificationsApi = {
  list: () => api.get("/api/v1/notifications"),
  markAsRead: (id) => api.patch(`/api/v1/notifications/${id}/mark_as_read`),
  markAllAsRead: () => api.post("/api/v1/notifications/mark_all_as_read"),
}

// Profile
export const profileApi = {
  get: () => api.get("/api/v1/profile"),
  update: (data) => api.patch("/api/v1/profile", data),
  changePassword: (currentPassword, password, passwordConfirmation) =>
    api.post("/api/v1/profile/change_password", {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    }),
}
